import type { TFunction } from "i18next";

import { uuidv4 } from "../utils/uuid";
import type { FormSection } from "./site";

export type FormFieldType =
  | "SHORT_TEXT"
  | "LONG_TEXT"
  | "EMAIL"
  | "PHONE"
  | "NUMBER"
  | "DATE"
  | "DOCUMENT"
  | "SELECT"
  | "CHECKBOX";

export type DateFieldFormat = "DATE" | "DATETIME" | "TIME" | "MONTH_YEAR";
export type PhoneFieldFormat = "BR" | "INTERNATIONAL";
export type SelectFieldDisplay = "DROPDOWN" | "RADIO" | "CHECKBOX";

export const DATE_FIELD_FORMATS: DateFieldFormat[] = ["DATE", "DATETIME", "TIME", "MONTH_YEAR"];
export const SELECT_FIELD_DISPLAYS: SelectFieldDisplay[] = ["DROPDOWN", "RADIO", "CHECKBOX"];

/** The four fixed fields forms had before custom fields; kept in sync for the current page renderer. */
export type LegacyFieldKey = "name" | "email" | "phone" | "message";

export interface FormFieldOption {
  id: string;
  label: string;
  /** false hides the option from the page (absent = visible). */
  active?: boolean;
}

export interface FormCustomField {
  id: string;
  /** Shown to visitors as the field's placeholder (or the checkbox text). */
  label: string;
  type: FormFieldType;
  required: boolean;
  /** false hides the field from the page (absent = visible). */
  active?: boolean;
  /** Choices of a SELECT field, in order. */
  options?: FormFieldOption[];
  /** How a SELECT field is shown: dropdown, single choice (radio) or multiple choice (checkboxes). */
  selectDisplay?: SelectFieldDisplay;
  /** What a DATE field asks for. */
  dateFormat?: DateFieldFormat;
  /** Whether a PHONE field takes only Brazilian numbers or any country (with a country code picker). */
  phoneFormat?: PhoneFieldFormat;
  /** Set on the fields converted from the old fixed ones, so they keep mapping to them. */
  legacyKey?: LegacyFieldKey;
}

/** Field types in the order they're offered when creating a field. */
export const FORM_FIELD_TYPES: FormFieldType[] = [
  "SHORT_TEXT",
  "LONG_TEXT",
  "EMAIL",
  "PHONE",
  "NUMBER",
  "DATE",
  "DOCUMENT",
  "SELECT",
  "CHECKBOX",
];

const LEGACY_FIELDS: { key: LegacyFieldKey; type: FormFieldType }[] = [
  { key: "name", type: "SHORT_TEXT" },
  { key: "email", type: "EMAIL" },
  { key: "phone", type: "PHONE" },
  { key: "message", type: "LONG_TEXT" },
];

/**
 * The fields of a form: its custom list when it has one, otherwise the old fixed fields that were
 * active (a brand-new form gets all four). Old fixed fields were always required.
 */
export const fieldsOf = (section: FormSection, t: TFunction): FormCustomField[] => {
  if (section.customFields) {
    return section.customFields;
  }
  return LEGACY_FIELDS.filter(({ key }) => section.fields?.[key]?.active ?? true).map(({ key, type }) => ({
    id: uuidv4(),
    label: t(`pages.editor.form.form.fields.${key}`),
    type,
    required: true,
    legacyKey: key,
  }));
};

/** The old `fields` object derived from the list, so pages rendered by the current package keep working. */
export const legacyFieldsOf = (fields: FormCustomField[]): FormSection["fields"] => {
  const isOn = (key: LegacyFieldKey) => visibleFields(fields).some(field => field.legacyKey === key);
  return {
    name: { active: isOn("name") },
    email: { active: isOn("email") },
    phone: { active: isOn("phone") },
    message: { active: isOn("message") },
  };
};

export const visibleFields = (fields: FormCustomField[]) => fields.filter(field => field.active !== false);

export const newFieldOption = (): FormFieldOption => ({ id: uuidv4(), label: "" });

export const newFormField = (): FormCustomField => ({
  id: uuidv4(),
  label: "",
  type: "SHORT_TEXT",
  required: true,
});

/** Visitors must leave a way to be contacted back. */
export const hasContactField = (fields: FormCustomField[]) =>
  visibleFields(fields).some(field => field.type === "EMAIL" || field.type === "PHONE");

/** Mailchimp adds contacts by e-mail, so it needs an e-mail field. */
export const hasEmailField = (fields: FormCustomField[]) => visibleFields(fields).some(field => field.type === "EMAIL");

/** The chosen variant of a field (date format, phone format, select display), with its default. */
export const variantOf = (field: FormCustomField): string | undefined => {
  switch (field.type) {
    case "DATE":
      return field.dateFormat || "DATE";
    case "PHONE":
      return field.phoneFormat || "BR";
    case "SELECT":
      return field.selectDisplay || "DROPDOWN";
    default:
      return undefined;
  }
};
